/*** IMPORTS FROM imports-loader ***/
import hydrator from "@gjensidige/views-api/src/runtime/hydrator";
import Component from ".";

/* global hydrator, Component */

// create a map of what we have hydrated, share it on window
window.__HYDRATED__ = window.__HYDRATED__ || {};

// get the current script executing
const partSrc = window.document?.currentScript?.getAttribute('src');

// filter the ones that need to be hydrated (data-nodeid contains the id for the props -> only hydration on node id's)
const parts = Array.from(
  document.querySelectorAll('script[src="' + partSrc + '"]')
).filter((part) => part.getAttribute('data-nodeid'));

// if this selection of parts contains elements that needs to be hydrated, add an eventListener
if (parts.length && window.addEventListener) {
  window.addEventListener('load', function () {
    parts.forEach((part) => {
      // make sure we only hydrate our components once
      const NODE_ID = part.getAttribute('data-nodeid');
      if (!window.__HYDRATED__[NODE_ID]) {
        window.__HYDRATED__[NODE_ID] = true;
        hydrator(Component, NODE_ID);
      }
    });
  });
}


import '@gjensidige/nci-core-typography/lib-css/index.css';
import '@gjensidige/nci-core-grid/lib-css/index.css';

import React from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types/prop-types';
import { Button } from '@gjensidige/core-components/lib/button/index';
import utils from '@gjensidige/core-utilities-css';
import { Col } from '@gjensidige/nci-core-grid/lib-css/col';
import { Container } from '@gjensidige/nci-core-grid/lib-css/container';
import { Row } from '@gjensidige/nci-core-grid/lib-css/row';
import { Title } from '@gjensidige/nci-core-typography/lib-css/title';

import { HeaderFullWidthPhotoProps as Props } from './types';

import styles from '../less/header-full-width-photo.module.less';

export const HeaderFullWidthPhoto = ({
  title,
  preface,
  image,
  alignment = 'right',
  buttons
}: Props) => (
  <Container
    as={'header'}
    fluid
    className={classNames(styles.overrides, styles.marginBHeaders)}
  >
    <Row>
      <Col md={12}>
        <Row
          className={classNames(utils['align-content-center'], styles.imageRow)}
        >
          {image && (
            <Col
              md={12}
              className={classNames(utils['align-self-md-end'], utils['p-0'])}
            >
              <img
                src={image.url}
                srcSet={image.srcset}
                alt={image.alt ?? ''}
                className={classNames(utils['p-0'], styles.photo)}
                width={16}
                height={9}
                sizes={
                  image.srcset &&
                  `(min-width: 1024px) and (max-width: 1199px) 1024px,
                    (min-width: 1200px) and (max-width: 1619px) 1200px,
                    (min-width: 1620px) 1620px,
                    100vw`
                }
                loading="lazy"
              />
            </Col>
          )}
          <Col
            sm={12}
            className={classNames(utils['d-lg-none'], utils['px-0'])}
          >
            <MobileInfoBlock
              title={title}
              preface={preface}
              buttons={buttons}
            />
          </Col>
        </Row>
        <DesktopInfoBlock
          title={title}
          preface={preface}
          alignment={alignment}
          buttons={buttons}
        />
      </Col>
    </Row>
  </Container>
);

HeaderFullWidthPhoto.propTypes = {
  title: PropTypes.string.isRequired,
  preface: PropTypes.string.isRequired,
  alignment: PropTypes.string.isRequired,
  image: PropTypes.any.isRequired,
  buttons: PropTypes.array
};

HeaderFullWidthPhoto.CSR = true;

export default HeaderFullWidthPhoto;

const MobileInfoBlock = ({ title, preface, buttons }: Props) => {
  const backgroundColor = 'blue';
  return (
    <Row
      className={classNames(
        styles.mobileInfoBox,
        styles[`${backgroundColor}Background`],
        utils['align-content-start'],
        utils['align-items-start'],
        utils['text-align-sm-left'],
        utils['justify-content-between']
      )}
    >
      <Col sm={12} className={utils['px-0']}>
        {title && (
          <Title tag="h1" size="h2" className={styles.title} text={title} />
        )}
        {preface && (
          <div
            className={classNames(styles.preface)}
            dangerouslySetInnerHTML={{ __html: preface }}
          ></div>
        )}
      </Col>
      {!!buttons?.length && <Buttons buttons={buttons} />}
    </Row>
  );
};
const DesktopInfoBlock = ({ title, preface, alignment, buttons }: Props) => {
  const backgroundColor = 'blue';
  return (
    <Row
      className={classNames(
        styles.absoluteRow,
        utils['px-2'],
        utils['align-content-center'],
        utils['align-items-center'],
        utils['text-align-sm-left'],
        utils['justify-content-between']
      )}
    >
      <Container>
        <Col
          md={alignment === 'left' ? { span: 7 } : { offset: 5, span: 7 }}
          xl={alignment === 'left' ? { span: 6 } : { offset: 6, span: 6 }}
        >
          <Row
            className={classNames(
              styles[`${backgroundColor}Background`],
              styles.desktopInfoBox
            )}
          >
            <Col md={12} className={utils['px-0']}>
              {title && (
                <Title
                  tag="h1"
                  size="h3"
                  className={styles.title}
                  text={title}
                />
              )}
              {preface && (
                <div
                  className={classNames(styles.preface)}
                  dangerouslySetInnerHTML={{ __html: preface }}
                ></div>
              )}
            </Col>
            {!!buttons?.length && <Buttons buttons={buttons} />}
          </Row>
        </Col>
      </Container>
    </Row>
  );
};

interface ButtonsProps {
  buttons: Props['buttons'];
}
const Buttons = ({ buttons }: ButtonsProps) => (
  <Col md="12" className={styles.buttonsWrapper}>
    {buttons.map((button, index) => (
      <Button
        key={button.text}
        variant={
          buttons.length === 1
            ? 'critical'
            : index === 0
            ? 'primary'
            : 'secondary'
        }
        data-theme="dark"
        href={button.url}
      >
        {button.text}
      </Button>
    ))}
  </Col>
);
